// src/lib/profilingApi.js
import { callAi } from "./geminiClient";

/** ──────────────────────────────────────────────────────────────────────────
 *  1) 프롬프트 빌더 (원문 유지)
 *  ────────────────────────────────────────────────────────────────────────── */
export function buildProfilingReportPrompt(lang, intakeData) {
  if (lang === "zh-CN") {
    return `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的首席 AI 诊断助手。你已学习了减肥治疗专业知识和多年的临床经验。你的任务是分析新患者的初始问诊数据，生成供“患者旅程协调员”在首次咨询中使用的“初诊患者画像”。请按照以下格式撰写，必须优先且最重要地写“患者画像一览”部分。

[患者问诊数据]
${intakeData}

[画像格式]
### ☀️ 患者画像一览
- **核心类型:** [一句话总结患者的核心特征]
- **主要问题:** [最需要解决的1-2个问题]
- **核心需求:** [患者目标及其背后真正的需求]
- **咨询要点:** [一句话提示咨询时的重点]

### 1. 患者类型初步诊断

### 2. 核心问题总结 (3个)
- 
- 
- 

### 3. 主要目标及潜在需求分析

### 4. 咨询时优先确认事项 (以获得共鸣和信任的教练式问题)
- 
- 

### 5. 可能的治疗障碍及预先应对策略
- **障碍:** 
- **应对策略:** `;
  }

  return `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 수석 AI 진단 어시스턴트입니다. 당신은 다이어트 치료 전문성과 다년간의 임상 노하우를 학습했습니다. 당신의 임무는 신규 환자의 초기 문진 데이터를 분석하여 '환자 여정 코디네이터'가 첫 상담에서 활용할 수 있는 '첫 방문 환자 프로파일링'을 생성하는 것입니다. 다음 문진 데이터를 분석하여 아래 형식에 맞춰 프로파일링을 작성해주세요. 반드시 '환자 프로필 한눈에 보기' 요약 섹션을 가장 먼저, 그리고 가장 중요하게 작성해야 합니다.

[환자 문진 데이터]
${intakeData}

[프로파일링 형식]
### ☀️ 환자 프로필 한눈에 보기
- **핵심 유형:** [환자의 핵심적인 특징을 한 문장으로 요약]
- **주요 문제점:** [가장 시급하게 해결해야 할 문제 1-2가지 요약]
- **핵심 니즈:** [환자가 말한 목표와 그 안에 숨겨진 진짜 욕구]
- **상담 포인트:** [상담 시 어떤 점에 초점을 맞춰야 할지 한 문장으로 제시]

### 1. 환자 유형 잠정 진단

### 2. 핵심 문제 요약 (3가지)
- 
- 
- 

### 3. 주요 목표 및 숨겨진 니즈 분석

### 4. 상담 시 우선 확인사항 (공감과 신뢰를 얻기 위한 코칭 질문 형태)
- 
- 

### 5. 예상 치료 장애물 및 선제적 대응 전략 제안
- **장애물:** 
- **대응 전략:** `;
}

export function buildFeaturePrompt(lang, type, intakeData) {
  const L = lang === "zh-CN";
  const t = (ko, zh) => (L ? zh : ko);

  switch (type) {
    case "mealPlan":
      return t(
        `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 영양사입니다. 다음 환자 정보를 바탕으로 체중 감량에 도움이 되는 **1일 한식 맞춤 식단**을 제안해주세요. 아침/점심/저녁/간식 + 간단 설명 포함.

[환자 정보]
${intakeData}

[출력 형식]
### ✨ AI 추천 1일 맞춤 식단...`,
        `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的营养师。根据以下患者信息，推荐有助于减重、符合中医原理的**一日三餐饮食方案**(含加餐) 并简述理由。

[患者信息]
${intakeData}

[输出格式]
### ✨ AI 推荐一日定制饮食...`
      );

    case "qa":
      return t(
        `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 베테랑 '환자 여정 코디네이터'입니다. 첫 상담에서 환자가 가장 궁금해할 **예상 질문 3가지**와 **이상적인 답변**을 작성해주세요.

[환자 정보]
${intakeData}

[출력 형식]
### ✨ 첫 상담 예상 Q&A

Q1:
A1:

Q2:
A2:

Q3:
A3:`,
        `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的资深“患者旅程协调员”。请生成首次咨询时最可能的**3个问题**及**理想回答**。

[患者信息]
${intakeData}

[输出格式]
### ✨ 首次咨询预期问答

Q1:
A1:

Q2:
A2:

Q3:
A3:`
      );

    case "lifestyle":
      return t(
        `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 수석 헬스 코치입니다. **수면/운동/스트레스 관리** 관점의 **생활습관 개선안**을 제안해주세요.

[환자 정보]
${intakeData}

[출력 형식]
### ✨ AI 추천 생활습관 개선안...`,
        `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的首席健康教练。基于以下信息，从**睡眠/运动/压力管理**三方面提出**可执行**的生活习惯改善建议。

[患者信息]
${intakeData}

[输出格式]
### ✨ AI 推荐生活习惯改善...`
      );

    case "exercisePlan":
      return t(
        `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 전문 운동 치료사입니다. 환자의 상태/체력 수준을 고려한 **주간 운동 계획**(준비운동/유산소/근력/정리운동; 종류/횟수/시간 명시)을 작성해주세요.

[환자 정보]
${intakeData}

[출력 형식]
### ✨ AI 추천 주간 운동 계획...`,
        `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的专业运动治疗师。结合患者状态与体能，制定**每周运动计划**(热身/有氧/力量/放松; 种类/次数/时间)。

[患者信息]
${intakeData}

[输出格式]
### ✨ AI 推荐每周运动计划...`
      );

    case "meditationScript":
      return t(
        `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 마음챙김 명상 전문가입니다. **스트레스/수면** 개선을 돕는 **2~3분 명상 스크립트**를 작성해주세요. 초보자도 따라하기 쉬운 차분한 어조.

[환자 정보]
${intakeData}

[출력 형식]
### ✨ 편안한 잠을 위한 마음챙김 명상...`,
        `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的正念冥想专家。撰写**2-3分钟冥想脚本**帮助缓解压力与改善睡眠，语气温和，初学者友好。

[患者信息]
${intakeData}

[输出格式]
### ✨ 安睡冥想脚本...`
      );

    case "snackAlternative":
      return t(
        `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 창의적인 영양사입니다. 야식을 **부담 없는 건강 야식 3가지**로 대체해주세요. 만들기 쉬운 메뉴 + 간단 이유.

[환자 정보]
${intakeData}

[출력 형식]
### ✨ AI 추천 건강 야식 레시피...`,
        `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的创意营养师。将夜宵替换为**3种更健康且美味的选择**，附简要理由与易行做法。

[患者信息]
${intakeData}

[输出格式]
### ✨ AI 推荐健康宵夜食谱...`
      );

    default:
      return intakeData;
  }
}

/** ──────────────────────────────────────────────────────────────────────────
 *  2) 파서 + HTML 빌더 (아이콘 주입 추가)
 *  ────────────────────────────────────────────────────────────────────────── */
function boldify(s) {
  return s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
}
function renderMdToHtml(text, { iconHTML } = {}) {
  const lines = String(text).trim().split(/\r?\n/);
  let html = "";
  let inList = false;
  let inTable = false;

  const closeList = () => { if (inList) { html += "</ul>"; inList = false; } };
  const closeTable = () => { if (inTable) { html += "</tbody></table></div>"; inTable = false; } };
  const closeAll = () => { closeList(); closeTable(); };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    if (line.startsWith("|") && line.endsWith("|")) {
      closeList(); // close list if it was open
      const isSeparator = /^\|(?:\s*:?-+:?\s*\|)+$/.test(line);
      
      if (isSeparator) {
        if (!inTable) {
           html += `<div style="overflow-x:auto;"><table class="report-table" style="width:100%; border-collapse:collapse; margin:10px 0; font-size:14px; text-align:left;"><tbody>`;
           inTable = true;
        }
        continue;
      }

      const cols = line.split("|").slice(1, -1).map(s => s.trim());
      
      if (!inTable) {
        html += `<div style="overflow-x:auto;"><table class="report-table" style="width:100%; border-collapse:collapse; margin:10px 0; font-size:14px; text-align:left; line-height:1.5;">`;
        html += `<thead><tr>`;
        cols.forEach(c => html += `<th style="border:1px solid #ddd; padding:10px; background-color:#f8f9fa;">${boldify(c)}</th>`);
        html += `</tr></thead><tbody>`;
        inTable = true;
      } else {
        html += `<tr>`;
        cols.forEach(c => html += `<td style="border:1px solid #ddd; padding:10px;">${boldify(c)}</td>`);
        html += `</tr>`;
      }
      continue;
    }

    closeTable();

    if (line.startsWith("### ")) { closeAll(); html += `<h3 class="report-title">${boldify(line.slice(4))}</h3>`; }
    else if (line.startsWith("#### ")) { closeAll(); html += `<h4 class="report-subtitle">${boldify(line.slice(5))}</h4>`; }
    else if (line.startsWith("- ")) { 
      if (!inList) { html += `<ul class="report-list">`; inList = true; } 
      html += `<li>${boldify(line.slice(2))}</li>`; 
    }
    else { closeAll(); html += `<p class="report-paragraph">${boldify(line)}</p>`; }
  }
  closeAll();

  // 섹션의 첫 h3 앞에 아이콘 주입
  if (iconHTML) {
    html = html.replace(
      /<h3 class="report-title">/,
      `<h3 class="report-title">${iconHTML} `
    );
  }
  return html;
}

function splitSections(answer) {
  const text = String(answer).replace(/\r\n/g, "\n");

  const summaryRegex =
    /###\s*(?:☀️\s*)?(?:환자\s*프로필\s*(?:한\s*눈|한눈)\s*에\s*보(?:기)?|환자\s*프로필\s*요약|患者(?:画像|档案)[^#\n]*)[\s\S]*?(?=\n###\s*\d+[.)]|\n###\s|$)/i;

  const numbered = [
    /###\s*1[.)]\s*[^\n]*/i, 
    /###\s*2[.)]\s*[^\n]*/i,
    /###\s*3[.)]\s*[^\n]*/i,
    /###\s*4[.)]\s*[^\n]*/i,
    /###\s*5[.)]\s*[^\n]*/i,
  ];
  const nextAny = /\n###\s*\d+[.)]|\n###\s/i;

  const summaryM = text.match(summaryRegex);
  const summary = summaryM ? summaryM[0].trim() : null;

  const grab = (startRe) => {
    const m = text.match(startRe);
    if (!m) return null;
    const from = m.index + 0;
    const after = from + m[0].length;
    const rest = text.slice(after);
    const n = rest.search(nextAny);
    const end = n >= 0 ? after + n : text.length;
    return text.slice(m.index, end).trim();
  };

  const s1 = grab(numbered[0]);
  const s2 = grab(numbered[1]);
  const s3 = grab(numbered[2]);
  const s4 = grab(numbered[3]);
  const s5 = grab(numbered[4]);

  return { summary, s1, s2, s3, s4, s5 };
}

// 섹션 타이틀용 SVG 아이콘 (요약 제외: 1~5)
const ICONS = [
  // 1. 환자 유형 잠정 진단
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>`,
  // 2. 핵심 문제 요약
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>`,
  // 3. 주요 목표/니즈
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.916 17.916 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" /></svg>`,
  // 4. 상담 우선 확인사항
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" /></svg>`,
  // 5. 장애물/대응 전략
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>`,
];

function buildSectionedHtml(sections) {
  const blocks = [];
  if (sections.summary)
    blocks.push(
      `<div class="profiling-block profiling-summary">${renderMdToHtml(sections.summary)}</div>`
    );
  if (sections.s1)
    blocks.push(
      `<div class="profiling-block profiling-type">${renderMdToHtml(sections.s1, { iconHTML: ICONS[0] })}</div>`
    );
  if (sections.s2)
    blocks.push(
      `<div class="profiling-block profiling-key-issues">${renderMdToHtml(sections.s2, { iconHTML: ICONS[1] })}</div>`
    );
  if (sections.s3)
    blocks.push(
      `<div class="profiling-block profiling-core-needs">${renderMdToHtml(sections.s3, { iconHTML: ICONS[2] })}</div>`
    );
  if (sections.s4)
    blocks.push(
      `<div class="profiling-block profiling-checklist">${renderMdToHtml(sections.s4, { iconHTML: ICONS[3] })}</div>`
    );
  if (sections.s5)
    blocks.push(
      `<div class="profiling-block profiling-barriers">${renderMdToHtml(sections.s5, { iconHTML: ICONS[4] })}</div>`
    );
  return blocks.join("\n");
}

/** 외부에서 바로 쓸 수 있게 export (선택) */
export function formatProfilingHtml(answer) {
  return buildSectionedHtml(splitSections(answer));
}

/** ──────────────────────────────────────────────────────────────────────────
 *  3) API 래퍼
 *  ────────────────────────────────────────────────────────────────────────── */
export const ProfilingAI = {
  async report({ intakeData }) {
    const lang =
      (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
    const userStr =
      (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user) {
      alert(lang === "zh-CN" ? "请先登录后再使用。" : "로그인 후 사용해 주세요");
      if (typeof window !== "undefined") window.location.href = "/login";
      throw new Error("NOT_AUTHENTICATED");
    }

    const prompt = buildProfilingReportPrompt(lang, intakeData);

    const contents = [{ role: "user", parts: [{ text: prompt }] }];

    const data = await callAi({
      contents,
      api_name: "profiling",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });

    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text;
    if (!answer) throw new Error("응답 형식이 올바르지 않습니다.");

    const sections = splitSections(answer);
    const html = buildSectionedHtml(sections);

    return { answer, html, sections };
  },

  async feature({ type, intakeData }) {
    const lang =
      (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
    const userStr =
      (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user) {
      alert(lang === "zh-CN" ? "请先登录后再使用。" : "로그인 후 사용해 주세요");
      if (typeof window !== "undefined") window.location.href = "/login";
      throw new Error("NOT_AUTHENTICATED");
    }

    const prompt = buildFeaturePrompt(lang, type, intakeData);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];

    const data = await callAi({
      contents,
      api_name: `profiling:${type}`,
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });

    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text;
    if (!answer) throw new Error("응답 형식이 올바르지 않습니다.");

    const html = `<div class="profiling-block profiling-feature-${type}">${renderMdToHtml(
      answer
    )}</div>`;

    return { answer, html };
  },
};
